/**
 * The empty state greeting, and the four ways to start.
 *
 * The greeting and the actions are separated on purpose. `AnalystHero` renders
 * only the words, because the buttons live in one place below the composer and
 * are shown whether or not the conversation has started -- rendering them here
 * too put two identical rows on the opening screen.
 *
 * Each action carries its own intent rather than relying on the wording of a
 * prompt, because only the dashboard action starts the guided interview and the
 * runtime must be told which button was pressed rather than inferring it.
 *
 * Report opens its own small interview and then reuses the governed document
 * planner, renderer, persistence, refresh, and export path.
 */
import { BarChart3, FileText, Search, Shapes } from "lucide-react";
import type { AgentIntent } from "../../services/agui";

/**
 * The step that opens the Analyze Data interview.
 *
 * Mirrors `clarify.ANALYZE_ENTRY_STEP` in the runtime. Steps cross the wire as
 * opaque strings, so this is a shared constant by convention rather than by
 * type -- the runtime treats an unrecognised step as a plain question, which is
 * the safe way for the two to disagree.
 */
export const ANALYZE_ENTRY_STEP = "ANALYZE_START";

/**
 * The step that opens the Create Artifact interview.
 *
 * Mirrors `clarify.ARTIFACT_ENTRY_STEP`. Needed for the same reason Analyze Data
 * needs one: the intent alone cannot tell the runtime that a button was pressed
 * rather than a sentence typed, and without the step the runtime treats the turn
 * as a plain question and the interview never opens.
 */
export const ARTIFACT_ENTRY_STEP = "ARTIFACT_START";

/** Opens the report-specific interview without changing artifact behavior. */
export const REPORT_ENTRY_STEP = "REPORT_START";

export interface HeroAction {
  id: string;
  label: string;
  intent: AgentIntent;
  /** The message the turn is opened with. */
  prompt: string;
  /**
   * The step the turn opens on, when the default entry step will not do.
   *
   * Analyze Data needs one. A typed message also arrives as the `analyze`
   * intent, so the intent alone cannot tell the runtime that a button was
   * pressed -- and interviewing every typed question would be a regression.
   * The step can, because only this action sends it.
   */
  workflowStep?: string;
  icon: JSX.Element;
}

export const HERO_ACTIONS: HeroAction[] = [
  {
    id: "dashboard",
    label: "Build Dashboard",
    intent: "dashboard",
    prompt: "Help me build a dashboard",
    icon: <BarChart3 size={15} aria-hidden="true" />,
  },
  {
    id: "analyze",
    label: "Analyze data",
    intent: "analyze",
    prompt: "Help me answer a question about my data",
    workflowStep: ANALYZE_ENTRY_STEP,
    icon: <Search size={15} aria-hidden="true" />,
  },
  {
    id: "report",
    label: "Build Report",
    intent: "report",
    prompt: "Help me build a governed report",
    workflowStep: REPORT_ENTRY_STEP,
    icon: <FileText size={15} aria-hidden="true" />,
  },
  {
    id: "artifact",
    label: "Create Artifact",
    intent: "artifact",
    prompt: "Help me create a document from my data",
    workflowStep: ARTIFACT_ENTRY_STEP,
    icon: <Shapes size={15} aria-hidden="true" />,
  },
];

export default function AnalystHero({ name = "AI Analyst" }: { name?: string }) {
  return (
    <section className="analyst-hero" aria-label={name}>
      <h1>I am {name}</h1>
      <p>I can help you create reports, dashboards, artifacts and analyze data.</p>
    </section>
  );
}
